import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  CreditCard,
  FileText,
  Lock,
  Sparkles,
  XCircle,
  Zap,
} from 'lucide-react';
import { useSubscription } from '../SubscriptionProvider';
import {
  CreatePaymentLegalAcceptanceDto,
  CreatePaymentRequestDto,
  OrderStatus,
  PaymentActionType,
  PaymentStatus,
  PlatformFeature,
  StartPaymentRequest,
  SubscriptionCheckoutResponseDto,
  SubscriptionInterval,
  SubscriptionCheckoutRequestDto,
  SubscriptionPlanListItemDto,
} from '../api';
import {
  buildSubscriptionCheckoutPricingSnapshot,
  getStripeCardCheckoutPaymentMethod,
} from '../features/subscriptionCheckoutPricing';
import {
  buildSubscriptionPlanComparison,
  buildSubscriptionPlanTaxBreakdown,
  getSubscriptionPlanGrossTotal,
  normalizeSubscriptionPlanCurrency,
} from '../features/subscriptionPlanPricing';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { usePublicLegalConfiguration } from '../platform/legalConfig';
import { runtimeConfig } from '../platform/runtime';
import { getSafeLinkAttributes } from '../platform/safeLinks';
import {
  clearPendingStripeCheckout,
  openStripeHostedCheckout,
  persistPendingStripeCheckout,
} from '../platform/stripeCheckout';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';

type BillingPeriod = 'monthly' | 'annual';
type PlanNameKey = 'proFeaturePlanMonthlyName' | 'proFeaturePlanAnnualName';
type PlanPeriodKey = 'proFeaturePeriodMonth' | 'proFeaturePeriodYear';
type PaymentFailureReason = 'payment' | 'technical';

interface PlanFeatureItem {
  key: string;
  params?: Record<string, string | number>;
  emphasized?: boolean;
}

// The authoritative value comes from the natal chart summary endpoint
// (maxAssociatedCharts); mirrored here for the plan comparison card with the
// same fallback used by MapaAstral.
const DEFAULT_MAX_ASSOCIATED_CHARTS = 5;

interface PlanSelection {
  code: string;
  nameKey: PlanNameKey;
  price: number;
  currency: string;
  periodKey: PlanPeriodKey;
  interval: SubscriptionInterval;
  intervalCount: number;
}

const ProFeature: React.FC<{ children: React.ReactNode; feature: PlatformFeature }> = ({ children, feature }) => {
  const { t, i18n } = useTranslation();
  const { status, hasFeature, loading, refresh } = useSubscription();
  const publicLegalConfiguration = usePublicLegalConfiguration();
  const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language;
  const api = useAstroApiClient();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | null>(null);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState<'success' | 'failed' | null>(null);
  const [paymentFailureReason, setPaymentFailureReason] = useState<PaymentFailureReason | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [paymentId, setPaymentId] = useState<string | undefined>(undefined);
  const [selectedPlan, setSelectedPlan] = useState<PlanSelection | null>(null);
  const [checkoutSnapshot, setCheckoutSnapshot] = useState<SubscriptionCheckoutResponseDto | null>(null);
  const [legalTermsAccepted, setLegalTermsAccepted] = useState(false);
  const [immediateStartConsentAccepted, setImmediateStartConsentAccepted] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlanListItemDto[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(false);
  const [plansReloadKey, setPlansReloadKey] = useState(0);
  const stripeBrowserListenerRef = useRef<{ remove: () => Promise<void> } | undefined>(undefined);
  const featureUnlocked = hasFeature(feature);

  const clearStripeBrowserListener = () => {
    const listener = stripeBrowserListenerRef.current;
    stripeBrowserListenerRef.current = undefined;
    if (listener) {
      void listener.remove();
    }
  };

  useEffect(
    () => () => {
      clearStripeBrowserListener();
    },
    [],
  );

  useEffect(() => {
    if (paymentResult === 'success') {
      refresh();
    }
  }, [paymentResult, refresh]);

  useEffect(() => {
    if (loading || status === 'unknown' || featureUnlocked) {
      return;
    }

    let cancelled = false;

    const loadPlans = async () => {
      setPlansLoading(true);
      setPlansError(false);

      try {
        const response = await api.apiSubscriptionPlans('EUR');

        if (!cancelled) {
          setSubscriptionPlans(response);
        }
      } catch (error) {
        if (!cancelled) {
          setSubscriptionPlans([]);
          setPlansError(true);
        }
      } finally {
        if (!cancelled) {
          setPlansLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      cancelled = true;
    };
  }, [api, featureUnlocked, loading, plansReloadKey, status]);

  const handleSubscribe = async (plan: PlanSelection) => {
    clearPendingStripeCheckout();

    try {
      const response = await api.apiSubscriptionCheckout(
        new SubscriptionCheckoutRequestDto({
          planCode: plan.code,
        }),
      );

      if (response.status === OrderStatus.Draft) {
        setOrderId(response.orderId);
        setCheckoutSnapshot(response);
        setSelectedPlan(plan);
        setShowPaymentModal(true);
        setPaymentMethod(null);
        setLegalTermsAccepted(false);
        setImmediateStartConsentAccepted(false);
      }
    } catch (error) {
      console.error('[v0] Failed to start subscription checkout:', error);
      alert(t('proFeatureStartSubscriptionError'));
    }
  };

  const handleSelectCard = async () => {
    const cardMethod = getStripeCardCheckoutPaymentMethod(
      buildSubscriptionCheckoutPricingSnapshot(checkoutSnapshot),
    );
    const methodCode = cardMethod?.methodCode;
    const providerCode = cardMethod?.providerCode;
    const legalAcceptance = checkoutSnapshot?.legalAcceptance;

    if (!methodCode || !providerCode) {
      alert(t('proFeaturePaymentMethodUnavailable'));
      return;
    }

    if (
      !legalAcceptance?.legalVersion ||
      !legalAcceptance.termsAndConditionsUrl ||
      !legalAcceptance.purchaseConditionsUrl ||
      !legalAcceptance.privacyPolicyUrl ||
      !legalAcceptance.disputeResolutionUrl
    ) {
      alert(t('proFeatureLegalAcceptanceUnavailable'));
      return;
    }

    if (!legalTermsAccepted || (legalAcceptance.immediateStartConsentRequired && !immediateStartConsentAccepted)) {
      alert(t('proFeatureLegalAcceptanceRequired'));
      return;
    }

    setIsCreatingPayment(true);
    setPaymentFailureReason(null);
    setPaymentResult(null);
    setPaymentId(undefined);
    clearPendingStripeCheckout();

    try {
      const response = await api.apiPaymentsPost(
        new CreatePaymentRequestDto({
          methodCode,
          providerCode,
          orderId,
          legalAcceptance: new CreatePaymentLegalAcceptanceDto({
            legalVersion: legalAcceptance.legalVersion,
            termsAndConditionsUrl: legalAcceptance.termsAndConditionsUrl,
            purchaseConditionsUrl: legalAcceptance.purchaseConditionsUrl,
            privacyPolicyUrl: legalAcceptance.privacyPolicyUrl,
            disputeResolutionUrl: legalAcceptance.disputeResolutionUrl,
            termsAndConditionsAccepted: true,
            purchaseConditionsAccepted: true,
            immediateStartConsentAccepted: legalAcceptance.immediateStartConsentRequired
              ? immediateStartConsentAccepted
              : false,
          }),
        }),
      );

      if (response.status === PaymentStatus.Created && response.paymentId) {
        setPaymentId(response.paymentId);
        setPaymentMethod('card');
      } else {
        setPaymentFailureReason('technical');
        setPaymentResult('failed');
      }
    } catch (error) {
      console.error('[v0] Failed to start subscription checkout for card:', error);
      setPaymentMethod(null);
      setPaymentId(undefined);
      setPaymentFailureReason('technical');
      setPaymentResult('failed');
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handleCardPayment = async () => {
    if (!paymentId) {
      return;
    }

    setPaymentFailureReason(null);
    setIsProcessing(true);

    try {
      const response = await api.apiPaymentsStart(
        new StartPaymentRequest({
          paymentId,
          methodDataJson: JSON.stringify({
            returnTarget: runtimeConfig.isNativePlatform ? 'native' : 'web',
          }),
        }),
      );

      if (response.status === PaymentStatus.Paid) {
        setIsProcessing(false);
        setPaymentResult('success');
        return;
      }

      const redirectUrl =
        response.action?.type === PaymentActionType.Redirect ? response.action.url : undefined;

      if (
        (response.status === PaymentStatus.Pending || response.status === PaymentStatus.Authorized) &&
        redirectUrl
      ) {
        const currentPaymentId = response.paymentId || paymentId;
        persistPendingStripeCheckout(currentPaymentId);
        clearStripeBrowserListener();

        const listener = await openStripeHostedCheckout(redirectUrl, () => {
          setIsProcessing(false);
          stripeBrowserListenerRef.current = undefined;
        });
        stripeBrowserListenerRef.current = listener;

        if (runtimeConfig.isNativePlatform) {
          setIsProcessing(false);
        }
        return;
      }

      if (
        response.status === PaymentStatus.Cancelled ||
        response.status === PaymentStatus.Failed ||
        response.status === PaymentStatus.Expired
      ) {
        // Payment failure (declined/cancelled/expired), distinct from a technical error.
        setIsProcessing(false);
        setPaymentFailureReason('payment');
        setPaymentResult('failed');
        return;
      }

      // No usable redirect and no terminal status: treat as a technical error.
      setIsProcessing(false);
      setPaymentFailureReason('technical');
      setPaymentResult('failed');
    } catch (error) {
      console.error('[v0] Failed to start subscription checkout for card:', error);
      setIsProcessing(false);
      setPaymentFailureReason('technical');
      setPaymentResult('failed');
    }
  };

  const handleCloseModal = () => {
    clearStripeBrowserListener();
    clearPendingStripeCheckout(paymentId);
    setShowPaymentModal(false);
    setPaymentMethod(null);
    setIsCreatingPayment(false);
    setIsProcessing(false);
    setPaymentResult(null);
    setPaymentFailureReason(null);
    setPaymentId(undefined);
    setOrderId(undefined);
    setCheckoutSnapshot(null);
    setSelectedPlan(null);
    setLegalTermsAccepted(false);
    setImmediateStartConsentAccepted(false);
  };

  const handleRetryPayment = () => {
    clearStripeBrowserListener();
    clearPendingStripeCheckout(paymentId);
    setPaymentResult(null);
    setPaymentFailureReason(null);
    setPaymentMethod(null);
    setPaymentId(undefined);
    setIsCreatingPayment(false);
    setIsProcessing(false);
  };

  const formatMoney = (amount: number, currency?: string) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizeSubscriptionPlanCurrency(currency),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatTaxRate = (rate: number) =>
    new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(rate);

  const formatLifecycleDuration = (duration: string, fallback: string) => {
    const match = /^P(\d+)([DMY])$/i.exec(duration);

    if (!match) {
      return fallback;
    }

    const count = Number(match[1]);
    const unit = match[2].toUpperCase();

    if (unit === 'D') {
      return t(count === 1 ? 'proFeatureCheckoutPeriodDay' : 'proFeatureCheckoutPeriodDays', { count });
    }

    if (unit === 'M') {
      return count === 1
        ? t('proFeatureLifecycleDurationMonth')
        : t('proFeatureCheckoutPeriodMonths', { count });
    }

    return count === 1
      ? t('proFeatureLifecycleDurationYear')
      : t('proFeatureCheckoutPeriodYears', { count });
  };

  const basicPlan = useMemo(
    () => subscriptionPlans.find((plan) => plan.code === 'basic'),
    [subscriptionPlans],
  );

  const monthlyPlan = useMemo(
    () =>
      subscriptionPlans.find((plan) => plan.code === 'pro-monthly') ??
      subscriptionPlans.find((plan) => plan.interval === SubscriptionInterval.Months && plan.intervalCount === 1),
    [subscriptionPlans],
  );

  const annualPlan = useMemo(
    () =>
      subscriptionPlans.find((plan) => plan.code === 'pro-yearly') ??
      subscriptionPlans.find((plan) => plan.interval === SubscriptionInterval.Years && plan.intervalCount === 1),
    [subscriptionPlans],
  );

  const selectedBillingPlan = useMemo<PlanSelection | null>(() => {
    const plan = billingPeriod === 'monthly' ? monthlyPlan : annualPlan;

    if (!plan?.code) {
      return null;
    }

    return {
      code: plan.code,
      nameKey: billingPeriod === 'monthly' ? 'proFeaturePlanMonthlyName' : 'proFeaturePlanAnnualName',
      price: getSubscriptionPlanGrossTotal(plan),
      currency: normalizeSubscriptionPlanCurrency(plan.currency),
      periodKey: billingPeriod === 'monthly' ? 'proFeaturePeriodMonth' : 'proFeaturePeriodYear',
      interval: plan.interval ?? (billingPeriod === 'monthly' ? SubscriptionInterval.Months : SubscriptionInterval.Years),
      intervalCount: plan.intervalCount ?? 1,
    };
  }, [annualPlan, billingPeriod, monthlyPlan]);

  const planComparison = useMemo(() => buildSubscriptionPlanComparison(monthlyPlan, annualPlan), [annualPlan, monthlyPlan]);

  const basicPriceLabel = formatMoney(getSubscriptionPlanGrossTotal(basicPlan), basicPlan?.currency);
  const monthlyPriceLabel = formatMoney(planComparison.monthlyTotal, monthlyPlan?.currency);
  const annualTotalPriceLabel = formatMoney(planComparison.annualTotal, annualPlan?.currency);
  const annualMonthlyPriceLabel = formatMoney(planComparison.annualEquivalentMonthlyTotal, annualPlan?.currency);
  const annualReferencePriceLabel = formatMoney(planComparison.annualReferenceTotal, annualPlan?.currency);
  const annualSavingsLabel = formatMoney(planComparison.annualSavings, annualPlan?.currency);
  const showAnnualComparison = planComparison.hasComparableSavings;
  const annualDiscountPercentage = planComparison.annualDiscountPercentage;
  const plansUnavailable = !plansLoading && (!monthlyPlan || !annualPlan);

  // Plan comparison copy mirrors the public landing page (landing/index-3d.html,
  // #pricing) so the pre-contractual promise is identical on both surfaces.
  const basicFeatureKeys = [
    'proFeatureBasicFeatureNatalChartFull',
    'proFeatureBasicFeatureDashboardLite',
    'proFeatureBasicFeatureInsightsLite',
    'proFeatureBasicFeatureCalendar',
    'proFeatureBasicFeatureInvestmentsDisclaimer',
  ] as const;

  // The Pro chat is metered against the plan's monthly credit allowance, so the
  // bullet states the allowance whenever the plan reports one.
  const proMonthlyCredits = (billingPeriod === 'monthly' ? monthlyPlan : annualPlan)?.monthlyCreditsIncluded ?? 0;

  const premiumFeatures: PlanFeatureItem[] = [
    { key: 'proFeaturePremiumFeatureAllBasic', emphasized: true },
    { key: 'proFeaturePremiumFeatureDailyDashboard' },
    {
      key: 'proFeaturePremiumFeatureLinkedCharts',
      params: { max: DEFAULT_MAX_ASSOCIATED_CHARTS },
    },
    { key: 'proFeaturePremiumFeatureDailyWeeklyMonthlyReadings' },
    proMonthlyCredits > 0
      ? {
          key: 'proFeaturePremiumFeatureAiChatCredits',
          params: { credits: proMonthlyCredits },
          emphasized: true,
        }
      : { key: 'proFeaturePremiumFeatureAiChat', emphasized: true },
  ];

  const selectedPlanName = selectedPlan ? t(selectedPlan.nameKey) : '';
  const selectedPlanPeriod = selectedPlan ? t(selectedPlan.periodKey) : '';
  const checkoutPricingSnapshot = useMemo(
    () => buildSubscriptionCheckoutPricingSnapshot(checkoutSnapshot),
    [checkoutSnapshot],
  );
  const stripeCardCheckoutMethod = useMemo(
    () => getStripeCardCheckoutPaymentMethod(checkoutPricingSnapshot),
    [checkoutPricingSnapshot],
  );
  const cardPaymentMethodLabel = t('proFeatureCardMethodName');
  const acceptedPaymentMethodLabels = stripeCardCheckoutMethod ? [cardPaymentMethodLabel] : [];
  const selectedCheckoutLegalAcceptance = checkoutSnapshot?.legalAcceptance;
  const checkoutLegalAcceptanceReady = Boolean(
    selectedCheckoutLegalAcceptance?.legalVersion &&
      selectedCheckoutLegalAcceptance.termsAndConditionsUrl &&
      selectedCheckoutLegalAcceptance.purchaseConditionsUrl &&
      selectedCheckoutLegalAcceptance.privacyPolicyUrl &&
      selectedCheckoutLegalAcceptance.disputeResolutionUrl,
  );
  const automaticRenewal = publicLegalConfiguration.commercialOfferings.automaticRenewal;
  const selectedCheckoutTotal = formatMoney(checkoutPricingSnapshot.total, checkoutPricingSnapshot.currency);
  const selectedCheckoutSubtotal = formatMoney(checkoutPricingSnapshot.subtotal, checkoutPricingSnapshot.currency);
  const selectedCheckoutTaxAmount = formatMoney(checkoutPricingSnapshot.taxAmount, checkoutPricingSnapshot.currency);
  const selectedCheckoutDiscount = formatMoney(checkoutPricingSnapshot.discount, checkoutPricingSnapshot.currency);
  const selectedCheckoutTaxLabel = checkoutPricingSnapshot.hasTax
    ? t('proFeatureCheckoutTaxWithRate', { rate: formatTaxRate(checkoutPricingSnapshot.taxRate) })
    : t('proFeatureCheckoutTax');
  const selectedCheckoutPlanName = checkoutPricingSnapshot.planName || selectedPlanName || t('proFeaturePremiumPlan');
  const selectedCheckoutPeriod = (() => {
    const intervalCount = checkoutPricingSnapshot.intervalCount || selectedPlan?.intervalCount || 1;
    const interval = checkoutPricingSnapshot.interval || selectedPlan?.interval;

    switch (interval) {
      case SubscriptionInterval.Days:
        return t(intervalCount === 1 ? 'proFeatureCheckoutPeriodDay' : 'proFeatureCheckoutPeriodDays', {
          count: intervalCount,
        });
      case SubscriptionInterval.Years:
        return intervalCount === 1
          ? t('proFeatureCheckoutPeriodAnnual')
          : t('proFeatureCheckoutPeriodYears', { count: intervalCount });
      case SubscriptionInterval.Months:
        return intervalCount === 1
          ? t('proFeatureCheckoutPeriodMonthly')
          : t('proFeatureCheckoutPeriodMonths', { count: intervalCount });
      default:
        return selectedPlanPeriod;
    }
  })();
  const selectedCheckoutLifecycleDuration = (() => {
    const interval = checkoutPricingSnapshot.interval || selectedPlan?.interval;

    switch (interval) {
      case SubscriptionInterval.Months:
        return formatLifecycleDuration(publicLegalConfiguration.lifecycle.monthlyDuration, selectedCheckoutPeriod);
      case SubscriptionInterval.Years:
        return formatLifecycleDuration(publicLegalConfiguration.lifecycle.annualDuration, selectedCheckoutPeriod);
      default:
        return selectedCheckoutPeriod;
    }
  })();
  const selectedCheckoutCredits =
    checkoutPricingSnapshot.monthlyCreditsIncluded > 0
      ? t('proFeatureCheckoutMonthlyCredits', { count: checkoutPricingSnapshot.monthlyCreditsIncluded })
      : t('proFeatureCheckoutNoCredits');
  const canCreateCheckoutPayment = Boolean(
    stripeCardCheckoutMethod?.methodCode &&
      stripeCardCheckoutMethod.providerCode &&
      checkoutLegalAcceptanceReady &&
      legalTermsAccepted &&
      (!selectedCheckoutLegalAcceptance?.immediateStartConsentRequired || immediateStartConsentAccepted),
  );
  const selectedCheckoutRenewalLabel = automaticRenewal
    ? t('proFeatureCheckoutAutomaticRenewalCompact', { period: selectedCheckoutLifecycleDuration })
    : t('proFeatureCheckoutManualRenewalCompact');
  const paymentFailureDescription =
    paymentFailureReason === 'technical'
      ? t('proFeaturePaymentTechnicalDescription')
      : t('proFeatureFailedDescription');
  const paymentFailureReasons =
    paymentFailureReason === 'technical'
      ? t('proFeaturePaymentTechnicalReasons')
      : t('proFeatureFailedReasons');
  const checkoutLegalLinks = useMemo(
    () => [
      {
        href: selectedCheckoutLegalAcceptance?.termsAndConditionsUrl || publicLegalConfiguration.legalPages.termsAndConditions,
        label: t('proFeatureLegalTerms'),
      },
      {
        href: selectedCheckoutLegalAcceptance?.purchaseConditionsUrl || publicLegalConfiguration.legalPages.purchaseConditions,
        label: t('proFeatureLegalPurchaseConditions'),
      },
      {
        href: publicLegalConfiguration.legalPages.cancellationAndRefunds,
        label: t('proFeatureLegalCancellationRefunds'),
      },
      {
        href: selectedCheckoutLegalAcceptance?.privacyPolicyUrl || publicLegalConfiguration.legalPages.privacyPolicy,
        label: t('proFeatureLegalPrivacy'),
      },
      {
        href: selectedCheckoutLegalAcceptance?.disputeResolutionUrl || publicLegalConfiguration.legalPages.disputeResolution,
        label: t('proFeatureLegalDisputeResolution'),
      },
    ],
    [publicLegalConfiguration.legalPages, selectedCheckoutLegalAcceptance, t],
  );

  const renderPlanTaxNote = (plan: SubscriptionPlanListItemDto | undefined) => {
    const breakdown = buildSubscriptionPlanTaxBreakdown(plan);
    const taxLabel = formatMoney(breakdown.taxAmount, plan?.currency);

    return (
      <p className="mt-2 text-xs text-slate-500">
        {breakdown.hasTax ? t('proFeatureTaxIncluded', { amount: taxLabel }) : t('proFeatureNoTaxApplied')}
      </p>
    );
  };

  const renderCheckoutSummaryRow = (
    label: string,
    value: React.ReactNode,
    className = 'text-slate-600',
  ) => (
    <div className={`flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${className}`}>
      <span className="shrink-0">{label}</span>
      <span className="break-words text-left font-medium sm:text-right">{value}</span>
    </div>
  );

  const renderCheckoutSummary = () => (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t('proFeatureCheckoutSummaryTitle')}
        </div>
        <p className="mt-1 text-xs leading-5 text-slate-500">{t('proFeatureCheckoutSummaryNote')}</p>
      </div>
      <div className="space-y-3 text-sm">
        <div className="space-y-2">
          {renderCheckoutSummaryRow(t('proFeatureCheckoutPlan'), selectedCheckoutPlanName, 'text-slate-700')}
          {renderCheckoutSummaryRow(t('proFeatureCheckoutPeriod'), selectedCheckoutLifecycleDuration, 'text-slate-700')}
          {renderCheckoutSummaryRow(t('proFeatureCheckoutCredits'), selectedCheckoutCredits, 'text-slate-700')}
          {renderCheckoutSummaryRow(
            t('proFeatureCheckoutBenefits'),
            t('proFeatureCheckoutBenefitsSummary'),
            'text-slate-700',
          )}
          {renderCheckoutSummaryRow(t('proFeatureCheckoutRenewal'), selectedCheckoutRenewalLabel, 'text-slate-700')}
        </div>
        <div className="space-y-2 border-t border-slate-200 pt-3">
          {renderCheckoutSummaryRow(t('proFeatureCheckoutSubtotal'), selectedCheckoutSubtotal)}
          {renderCheckoutSummaryRow(selectedCheckoutTaxLabel, selectedCheckoutTaxAmount)}
          {checkoutPricingSnapshot.hasDiscount
            ? renderCheckoutSummaryRow(t('proFeatureCheckoutDiscount'), selectedCheckoutDiscount)
            : null}
          <div className="border-t border-slate-200 pt-2">
            {renderCheckoutSummaryRow(
              t('proFeatureCheckoutTotal'),
              selectedCheckoutTotal,
              'font-semibold text-slate-800',
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCheckoutLegalLinks = () => (
    <div className="rounded-lg border border-action-100 bg-action-50/80 px-3 py-2.5">
      <div className="flex items-start gap-2">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-action-600" />
        <p className="text-xs leading-5 text-slate-700">
          {t('proFeatureLegalCompactPrefix')}{' '}
          {checkoutLegalLinks.map((link, index) => (
            <React.Fragment key={`${link.label}-${link.href}`}>
              <a
                href={link.href}
                {...getSafeLinkAttributes(link.href, 'legal')}
                className="font-semibold text-action-800 underline-offset-4 hover:underline"
              >
                {link.label}
              </a>
              {index < checkoutLegalLinks.length - 1 ? <span>, </span> : <span>.</span>}
            </React.Fragment>
          ))}
        </p>
      </div>
    </div>
  );

  const checkoutLegalCheckboxClassName =
    'mt-0.5 h-6 w-6 border-[3px] border-slate-700 bg-slate-100 ring-1 ring-slate-300 data-[state=checked]:border-action-800 data-[state=checked]:bg-action-700';

  const renderCheckoutLegalAcceptance = () => (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      {checkoutLegalAcceptanceReady ? (
        <div className="space-y-2.5">
          <div className="flex items-start gap-3">
            <Checkbox
              id="checkout-legal-terms"
              checked={legalTermsAccepted}
              onCheckedChange={(checked) => setLegalTermsAccepted(checked === true)}
              className={checkoutLegalCheckboxClassName}
            />
            <Label
              htmlFor="checkout-legal-terms"
              className="cursor-pointer text-xs font-medium leading-5 text-slate-700"
            >
              {t('proFeatureLegalAcceptanceLabel')}
            </Label>
          </div>
          {selectedCheckoutLegalAcceptance?.immediateStartConsentRequired ? (
            <div className="flex items-start gap-3">
              <Checkbox
                id="checkout-immediate-start-consent"
                checked={immediateStartConsentAccepted}
                onCheckedChange={(checked) => setImmediateStartConsentAccepted(checked === true)}
                className={checkoutLegalCheckboxClassName}
              />
              <Label
                htmlFor="checkout-immediate-start-consent"
                className="cursor-pointer text-xs font-medium leading-5 text-slate-700"
              >
                {t('proFeatureImmediateStartConsentLabel')}
              </Label>
            </div>
          ) : null}
          <p className="text-[11px] leading-4 text-slate-500">
            {t('proFeatureLegalVersionNotice', {
              version: selectedCheckoutLegalAcceptance?.legalVersion,
              date: selectedCheckoutLegalAcceptance?.legalLastUpdated,
            })}
          </p>
        </div>
      ) : (
        <p role="alert" className="text-xs font-medium leading-5 text-red-700">
          {t('proFeatureLegalAcceptanceUnavailable')}
        </p>
      )}
    </div>
  );

  const nextPaymentDate = useMemo(() => {
    if (!selectedPlan) {
      return '';
    }

    const nextDate = new Date();

    switch (selectedPlan.interval) {
      case SubscriptionInterval.Years:
        nextDate.setFullYear(nextDate.getFullYear() + selectedPlan.intervalCount);
        break;
      case SubscriptionInterval.Months:
        nextDate.setMonth(nextDate.getMonth() + selectedPlan.intervalCount);
        break;
      case SubscriptionInterval.Days:
        nextDate.setDate(nextDate.getDate() + selectedPlan.intervalCount);
        break;
      default:
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
    }

    return nextDate.toLocaleDateString(locale);
  }, [locale, selectedPlan]);

  const renderPaymentDialogHeader = () => {
    if (paymentResult) {
      return (
        <DialogHeader
          data-testid="payment-dialog-static-header"
          className="flex-shrink-0 border-b border-slate-100 bg-white px-6 pb-4 pr-14 pt-[calc(env(safe-area-inset-top)+1.5rem)] sm:p-6 sm:pr-14"
        >
          <DialogTitle className="text-2xl font-bold text-slate-800">
            {paymentResult === 'success' ? t('proFeaturePaymentConfirmed') : t('proFeaturePaymentNotConfirmed')}
          </DialogTitle>
        </DialogHeader>
      );
    }

    return (
      <DialogHeader
        data-testid="payment-dialog-static-header"
        className="flex-shrink-0 border-b border-slate-100 bg-white px-6 pb-4 pr-14 pt-[calc(env(safe-area-inset-top)+1.5rem)] sm:p-6 sm:pr-14"
      >
        <DialogTitle className="text-2xl font-bold text-slate-800">{t('proFeatureChoosePaymentMethod')}</DialogTitle>
        <DialogDescription className="text-slate-600">
          {t('proFeatureSubscriptionSummary', {
            name: selectedCheckoutPlanName,
            price: selectedCheckoutTotal,
            period: selectedCheckoutLifecycleDuration,
          })}
        </DialogDescription>
      </DialogHeader>
    );
  };

  if (loading || status === 'unknown') {
    return <div className="p-6 text-center text-slate-600">{t('proFeatureCheckingSubscription')}</div>;
  }

  if (featureUnlocked) {
    return <>{children}</>;
  }

  if (plansLoading) {
    return <div className="p-6 text-center text-slate-600">{t('loading')}</div>;
  }

  if (plansError || plansUnavailable) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="relative">
              <Lock className="h-16 w-16 text-action-500" />
              <Sparkles className="absolute -right-2 -top-2 h-8 w-8 text-amber-500" />
            </div>
          </div>
          <h2 className="mb-2 text-3xl font-bold text-slate-800">{t('proFeatureTitle')}</h2>
          <p className="mx-auto max-w-2xl text-slate-600">{t('proFeatureSubtitle')}</p>
        </div>

        <Card className="mx-auto max-w-2xl border-amber-200 bg-amber-50 shadow-sm">
          <CardContent className="space-y-4 p-6 text-center">
            <p className="text-sm text-slate-700">{t('proFeatureLoadPlansError')}</p>
            <Button
              onClick={() => setPlansReloadKey((prev) => prev + 1)}
              className="bg-action-700 text-white hover:bg-action-800"
            >
              {t('tryAgain')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mb-4 flex justify-center">
          <div className="relative">
            <Lock className="h-16 w-16 text-action-500" />
            <Sparkles className="absolute -right-2 -top-2 h-8 w-8 text-amber-500" />
          </div>
        </div>
        <h2 className="mb-2 text-3xl font-bold text-slate-800">{t('proFeatureTitle')}</h2>
        <p className="mx-auto max-w-2xl text-slate-600">{t('proFeatureSubtitle')}</p>
      </div>

      <div className="flex justify-center">
        <div className="inline-flex items-center rounded-lg border border-stone-200 bg-white p-1 shadow-sm">
          <Button
            variant={billingPeriod === 'monthly' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setBillingPeriod('monthly')}
            className={billingPeriod === 'monthly' ? 'bg-action-700 text-white hover:bg-action-800' : 'text-slate-600'}
          >
            {t('proFeatureBillingMonthly')}
          </Button>
          <Button
            variant={billingPeriod === 'annual' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setBillingPeriod('annual')}
            className={billingPeriod === 'annual' ? 'bg-action-700 text-white hover:bg-action-800' : 'text-slate-600'}
          >
            {t('proFeatureBillingAnnual')}
            {annualDiscountPercentage > 0 ? (
              <Badge className="ml-2 bg-green-500 text-xs text-white">-{annualDiscountPercentage}%</Badge>
            ) : null}
          </Button>
        </div>
      </div>

      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
        <Card className="border-stone-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-slate-800">
              <span>{t('proFeatureBasicPlan')}</span>
              <Badge variant="secondary" className="bg-stone-100 text-stone-700">
                {t('proFeatureFree')}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-3xl font-bold text-slate-800">
              {basicPriceLabel}
              <span className="text-sm font-normal text-slate-600">{t('proFeaturePerMonthSuffix')}</span>
            </div>
            {renderPlanTaxNote(basicPlan)}
            <ul className="space-y-2 text-sm text-slate-600">
              {basicFeatureKeys.map((featureKey) => (
                <li key={featureKey} className="flex items-start">
                  <Check className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  <span>{t(featureKey)}</span>
                </li>
              ))}
              <li className="flex items-start text-slate-400">
                <span className="mr-2">x</span>
                <span>{t('proFeatureBasicFeaturePremiumLocked')}</span>
              </li>
            </ul>
            <Button variant="outline" className="w-full bg-transparent" disabled>
              {t('proFeatureCurrentPlan')}
            </Button>
          </CardContent>
        </Card>

        <Card className="relative border-action-300 bg-gradient-to-br from-action-50 to-white shadow-lg">
          <div className="absolute left-1/2 top-[-0.75rem] -translate-x-1/2 transform">
            <Badge className="bg-action-700 text-white">
              <Sparkles className="mr-1 h-3 w-3" />
              {t('proFeatureRecommended')}
            </Badge>
          </div>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-slate-800">
              <span>{t('proFeaturePremiumPlan')}</span>
              <Badge className="bg-action-200 text-action-800">{t('proFeaturePopular')}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {billingPeriod === 'monthly' ? (
              <div>
                <div className="text-3xl font-bold text-slate-800">
                  {monthlyPriceLabel}
                  <span className="text-sm font-normal text-slate-600">{t('proFeaturePerMonthSuffix')}</span>
                </div>
                {renderPlanTaxNote(monthlyPlan)}
              </div>
            ) : (
              <div>
                <div className="text-3xl font-bold text-slate-800">
                  {annualMonthlyPriceLabel}
                  <span className="text-sm font-normal text-slate-600">{t('proFeaturePerMonthSuffix')}</span>
                </div>
                {showAnnualComparison ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-slate-500 line-through">
                      {annualReferencePriceLabel}
                      {t('proFeaturePerYearSuffix')}
                    </span>
                    <Badge className="bg-green-100 text-xs text-green-700">
                      {t('proFeatureSaveAmount', { amount: annualSavingsLabel })}
                    </Badge>
                  </div>
                ) : null}
                <p className="mt-2 text-xs text-slate-600">
                  {t('proFeatureBilledAnnually', { total: annualTotalPriceLabel })}
                </p>
                {renderPlanTaxNote(annualPlan)}
              </div>
            )}
            <ul className="space-y-2 text-sm text-slate-700">
              {premiumFeatures.map((feature) => (
                <li key={feature.key} className="flex items-start">
                  <Check className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  <span className={feature.emphasized ? 'font-medium' : ''}>
                    {t(feature.key, feature.params)}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => selectedBillingPlan && handleSubscribe(selectedBillingPlan)}
              disabled={!selectedBillingPlan}
              className="w-full bg-action-700 text-white hover:bg-action-800"
            >
              <Zap className="mr-2 h-4 w-4" />
              {t('proFeatureSubscribeNow')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="mx-auto max-w-3xl border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50">
        <CardContent className="p-6">
          <div className="flex items-start space-x-4">
            <Sparkles className="mt-1 h-6 w-6 flex-shrink-0 text-amber-600" />
            <div>
              <h3 className="mb-2 font-bold text-slate-800">{t('proFeatureGuaranteeTitle')}</h3>
              <p className="text-sm text-slate-600">{t('proFeatureGuaranteeDescription')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center text-sm text-slate-600">
        <p>{t('proFeatureSecurePaymentFooter')}</p>
      </div>

      <Dialog open={showPaymentModal} onOpenChange={handleCloseModal}>
        <DialogContent
          className="left-0 top-0 flex h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-none border-0 bg-white p-0 sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[92vh] sm:w-full sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:border"
          closeButtonClassName="absolute right-4 top-4 z-[60]"
        >
          {renderPaymentDialogHeader()}
          <div
            data-testid="payment-dialog-scroll-area"
            className="min-h-0 flex-1 overflow-y-auto p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] sm:p-6"
          >
          {paymentResult ? (
            <>
              <div className="space-y-6 py-6">
                <div className="flex flex-col items-center space-y-4">
                  {paymentResult === 'success' ? (
                    <>
                      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green-100">
                        <CheckCircle2 className="h-12 w-12 text-green-600" />
                      </div>
                      <div className="space-y-2 text-center">
                        <h3 className="text-xl font-bold text-slate-800">{t('proFeatureActivatedTitle')}</h3>
                        <p className="text-sm text-slate-600">{t('proFeatureActivatedDescription')}</p>
                      </div>
                      <div className="w-full rounded-lg border border-green-200 bg-green-50 p-4">
                        <div className="space-y-1 text-center">
                          <p className="text-sm font-semibold text-green-800">
                            {t('proFeatureChargedToday', { price: selectedCheckoutTotal })}
                          </p>
                          <p className="text-xs text-green-700">
                            {automaticRenewal
                              ? t('proFeatureNextPayment', { date: nextPaymentDate })
                              : t('proFeatureAccessUntil', { date: nextPaymentDate })}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
                        <XCircle className="h-12 w-12 text-red-600" />
                      </div>
                      <div className="space-y-2 text-center">
                        <h3 className="text-xl font-bold text-slate-800">{t('proFeatureFailedTitle')}</h3>
                        <p className="text-sm text-slate-600">{paymentFailureDescription}</p>
                      </div>
                      <div className="w-full rounded-lg border border-red-200 bg-red-50 p-4">
                        <p className="text-center text-sm text-red-700">{paymentFailureReasons}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {paymentResult === 'success' ? (
                <Button onClick={handleCloseModal} className="w-full bg-green-600 text-white hover:bg-green-700">
                  {t('proFeatureStartUsingPremium')}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button
                    onClick={handleRetryPayment}
                    className="w-full bg-action-700 text-white hover:bg-action-800"
                  >
                    {t('tryAgain')}
                  </Button>
                  <Button variant="outline" onClick={handleCloseModal} className="w-full bg-transparent">
                    {t('close')}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-4">
                {!paymentMethod ? (
                  <div className="grid grid-cols-1 gap-3">
                    {renderCheckoutSummary()}
                    {renderCheckoutLegalLinks()}
                    {renderCheckoutLegalAcceptance()}
                    {stripeCardCheckoutMethod ? (
                      <Button
                        variant="outline"
                        className="h-auto justify-start space-x-4 bg-transparent py-4 hover:border-slate-400 hover:bg-slate-50"
                        onClick={handleSelectCard}
                        disabled={!canCreateCheckoutPayment || isCreatingPayment}
                      >
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-50">
                          <CreditCard className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-slate-800">
                            {isCreatingPayment
                              ? t('proFeaturePreparingPayment')
                              : t('proFeatureContinueWithPaymentMethod', { method: cardPaymentMethodLabel })}
                          </div>
                          <div className="text-sm text-slate-600">{t('proFeatureCardRedirectHint')}</div>
                        </div>
                      </Button>
                    ) : (
                      <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        {t('proFeaturePaymentMethodUnavailable')}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPaymentMethod(null);
                        setPaymentId(undefined);
                      }}
                      className="mb-2"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {t('back')}
                    </Button>

                    {paymentMethod === 'card' && (
                      <div className="space-y-4">
                        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                          <div className="mb-2 flex items-center space-x-3">
                            <CreditCard className="h-5 w-5 text-indigo-600" />
                            <h3 className="font-semibold text-slate-800">{t('proFeatureCardPaymentTitle')}</h3>
                          </div>
                          <p className="text-sm text-slate-600">{t('proFeatureCardPaymentDescription')}</p>
                        </div>

                        <Button
                          onClick={handleCardPayment}
                          className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
                          disabled={isProcessing}
                        >
                          {isProcessing ? t('processing') : t('proFeatureCardContinueToPayment')}
                        </Button>

                        {renderCheckoutLegalLinks()}
                      </div>
                    )}

                    <div className="pt-2 text-center text-xs text-slate-500">
                      <Lock className="mr-1 inline h-3 w-3" />
                      {t('proFeatureSecureEncryptedPayment')}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProFeature;
